import { browser } from "$app/environment";
import * as publicEnv from "$env/static/public";
import { get, writable } from "svelte/store";

export type ConsentChoice = "granted" | "denied";

export type PrivacyConsentRecord = {
  policyVersion: number;
  updatedAt: string;
  analytics: ConsentChoice;
  externalMedia: ConsentChoice;
};

export type PrivacyConsentState = {
  initialized: boolean;
  record: PrivacyConsentRecord | null;
};

export const CURRENT_PRIVACY_POLICY_VERSION = 1;
export const PRIVACY_CONSENT_STORAGE_KEY = "gsc_privacy_consent";
export const LEGACY_ANALYTICS_CONSENT_STORAGE_KEY = "gsc_analytics_consent";
export const OPEN_PRIVACY_PREFERENCES_EVENT = "gsc:open-privacy-preferences";

export const privacyConsent = writable<PrivacyConsentState>({ initialized: false, record: null });

const isChoice = (value: unknown): value is ConsentChoice => value === "granted" || value === "denied";

const isUtcIsoTimestamp = (value: unknown): value is string => {
  if (typeof value !== "string" || !value.endsWith("Z")) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value;
};

export function isValidPrivacyConsentRecord(value: unknown): value is PrivacyConsentRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Partial<PrivacyConsentRecord>;
  return (
    Number.isInteger(candidate.policyVersion) &&
    isUtcIsoTimestamp(candidate.updatedAt) &&
    isChoice(candidate.analytics) &&
    isChoice(candidate.externalMedia)
  );
}

const readFlag = () => {
  const staticValue = (publicEnv as Record<string, string | undefined>).PUBLIC_PRIVACY_CONSENT_ENABLED;
  const viteValue = browser
    ? (import.meta.env as Record<string, string | undefined>).PUBLIC_PRIVACY_CONSENT_ENABLED
    : undefined;
  const processValue = typeof process === "undefined" ? undefined : process.env.PUBLIC_PRIVACY_CONSENT_ENABLED;
  const value = String(staticValue ?? viteValue ?? processValue ?? "");
  return value.trim().toLowerCase();
};

export const privacyConsentEnabled = () => readFlag() !== "false";

function currentRecordFromStorage(): PrivacyConsentRecord | null {
  try {
    const raw = window.localStorage.getItem(PRIVACY_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidPrivacyConsentRecord(parsed)) return null;
    return parsed.policyVersion === CURRENT_PRIVACY_POLICY_VERSION ? parsed : null;
  } catch {
    return null;
  }
}

function migrateLegacyConsent(): PrivacyConsentRecord | null {
  try {
    if (window.localStorage.getItem(PRIVACY_CONSENT_STORAGE_KEY) !== null) return null;
    const legacy = window.localStorage.getItem(LEGACY_ANALYTICS_CONSENT_STORAGE_KEY);
    if (!isChoice(legacy)) return null;

    const migrated: PrivacyConsentRecord = {
      policyVersion: CURRENT_PRIVACY_POLICY_VERSION,
      updatedAt: new Date().toISOString(),
      analytics: legacy,
      externalMedia: "denied",
    };
    window.localStorage.setItem(PRIVACY_CONSENT_STORAGE_KEY, JSON.stringify(migrated));
    window.localStorage.removeItem(LEGACY_ANALYTICS_CONSENT_STORAGE_KEY);
    return migrated;
  } catch {
    return null;
  }
}

export function initializePrivacyConsent(): PrivacyConsentState {
  if (!browser || !privacyConsentEnabled()) {
    const state = { initialized: true, record: null };
    privacyConsent.set(state);
    return state;
  }

  const record = currentRecordFromStorage() ?? migrateLegacyConsent();
  const state = { initialized: true, record };
  privacyConsent.set(state);
  return state;
}

export function getPrivacyConsentRecord(): PrivacyConsentRecord | null {
  const state = get(privacyConsent);
  return state.initialized ? state.record : initializePrivacyConsent().record;
}

export function setPrivacyConsent(choices: Pick<PrivacyConsentRecord, "analytics" | "externalMedia">) {
  if (!browser || !privacyConsentEnabled()) return null;

  const record: PrivacyConsentRecord = {
    policyVersion: CURRENT_PRIVACY_POLICY_VERSION,
    updatedAt: new Date().toISOString(),
    analytics: choices.analytics,
    externalMedia: choices.externalMedia,
  };

  try {
    window.localStorage.setItem(PRIVACY_CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // The in-memory choice still applies for this page, but a future visit will prompt again.
  }
  privacyConsent.set({ initialized: true, record });
  return record;
}

export function openPrivacyPreferences() {
  if (browser && privacyConsentEnabled()) {
    window.dispatchEvent(new CustomEvent(OPEN_PRIVACY_PREFERENCES_EVENT));
  }
}

export function __resetPrivacyConsentForTests() {
  privacyConsent.set({ initialized: false, record: null });
}
